import React, { useState, useEffect } from 'react';
import NotificationPreferences from './NotificationPreferences';
import { useAuth } from '../AuthContext';

// Numbers from the estimate / plan can be missing for some accounts (a plan
// without a monitoring figure, an upsell path without a quota): show "—"
// rather than crash the window (Karl Lightfoot, 3 Nov 2026).
const fmtNum = (n: any): string => (n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString());

interface MonitoringSettingsModalProps {
  projectId: number;
  projectName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface MonitoringPlan {
  id: string;
  name: string;
  price: number;
  priceLabel: string;
  includedChecks: number;
  costPerCheck: number;
  recommended?: boolean;
}

interface FrequencyOption {
  value: number;
  label: string;
  checksPerMonth: number;
  recommended?: boolean;
}

interface CostEstimate {
  projectEstimate: {
    candidateCount: number;
    estimatedMonthlyChecks: number;
    breakdown: { [key: number]: number };
    riskTierDistribution?: {
      highRisk: number;
      mediumRisk: number;
      lowRisk: number;
      stable: number;
    };
  };
  aggregateEstimate: {
    totalEstimatedChecks: number;
    existingChecks: number;
    newProjectChecks: number;
    projectBreakdown: Array<{
      projectId: number | string;
      projectName: string;
      estimatedChecks: number;
    }>;
  };
  currentSubscription?: {
    planId: string;
    planName?: string;
    includedChecks: number;
    sufficient: boolean;
  };
  recommendedPlan: MonitoringPlan | null; // none for Starter / Growth / Scale accounts
  needsUpgrade: boolean;
  availablePlans: MonitoringPlan[];
  tieredUpsell?: TieredUpsell | null;
}

interface UpsellPath {
  kind: 'stay' | 'tier_upgrade';
  tierKey: string;
  tierName: string;
  tierBaseQuota: number;
  tierMonthlyPence: number;
  boltOnsToAdd: number;
  boltOnUnitPricePence: number;
  boltOnQuotaPerUnit: number;
  totalQuotaAfter: number;
  totalMonthlyPence: number;
}

interface TieredUpsell {
  isTiered: boolean;
  neededChecks: number;
  currentQuota: number;
  shortfall: number;
  currentMonthlyPence: number;
  paths: UpsellPath[];
  recommended: UpsellPath | null;
  savingsVsStayPence: number;
}

const MonitoringSettingsModal: React.FC<MonitoringSettingsModalProps> = ({
  projectId,
  projectName,
  isOpen,
  onClose
}) => {
  const { user } = useAuth();
  // Members and viewers can look but not switch monitoring on/off or change it.
  const canManage = !user?.teamId || user?.teamRole === 'admin';
  const [frequencyMode, setFrequencyMode] = useState<'adaptive' | 'manual'>('adaptive');
  const [instantRunning, setInstantRunning] = useState(false);
  const [instantMsg, setInstantMsg] = useState('');
  const [instantOpId, setInstantOpId] = useState<string | null>(null);

  const handleStopInstantCheck = async () => {
    if (!instantOpId) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/monitoring/instant-check-cancel/${instantOpId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setInstantMsg('Stopping after the current batch…');
    } catch { /* polling will report the final state */ }
  };

  const handleInstantCheck = async () => {
    if (!window.confirm('Run an instant monitoring check for every monitored candidate in this project now? 1 monitoring credit per candidate will be consumed from your allowance.')) return;
    setInstantRunning(true);
    setInstantMsg('Starting…');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/monitoring/projects/${projectId}/instant-check`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start instant check');
      setInstantOpId(data.operationId);
      setInstantMsg(`Checking ${fmtNum(data.total)} candidate${data.total === 1 ? '' : 's'}…${data.capped ? ` (capped from ${fmtNum(data.candidateCount)} by your remaining allowance)` : ''}`);
      const poll = setInterval(async () => {
        try {
          const p = await fetch(`/api/monitoring/instant-check-progress/${data.operationId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (!p.ok) return;
          const op = await p.json();
          if (op.status === 'running') {
            setInstantMsg(`Checked ${fmtNum(op.done)} of ${fmtNum(op.total)}… (${op.withChanges} with changes so far)`);
          } else {
            clearInterval(poll);
            setInstantRunning(false);
            setInstantOpId(null);
            setInstantMsg(op.status === 'complete'
              ? `Done — ${fmtNum(op.done)} checked, ${op.withChanges} with changes${op.failed ? `, ${op.failed} failed` : ''}. Signals appear on the monitoring dashboard.`
              : op.status === 'cancelled'
              ? `Stopped — ${fmtNum(op.done)} of ${fmtNum(op.total)} checked, ${op.withChanges} with changes. Only checked candidates consumed credits.`
              : `Failed: ${op.error || 'unknown error'}`);
          }
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e: any) {
      setInstantRunning(false);
      setInstantMsg(e.message || 'Failed to start instant check');
    }
  };
  const [manualFrequency, setManualFrequency] = useState(90); // Default to quarterly
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState<CostEstimate | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [monitoringEnabled, setMonitoringEnabled] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [hasActiveProSubscription, setHasActiveProSubscription] = useState(false);
  const [showNotificationPrefs, setShowNotificationPrefs] = useState(false);

  // Tiered-plan state. Drives the "you have X included, this project will
  // use Y, leaving Z" banner shown to Starter/Growth/Scale customers in
  // place of the legacy "subscribe to a plan" flow.
  const [tierState, setTierState] = useState<{
    source: 'tiered' | 'grandfathered' | 'none';
    tierName: string | null;
    monthlyQuota: number;
    usedThisCycle: number;
    monthlyPricePence: number | null;
  } | null>(null);

  // Adaptive monitoring tier frequencies
  const [highRiskFrequency, setHighRiskFrequency] = useState(14); // Bi-weekly
  const [mediumRiskFrequency, setMediumRiskFrequency] = useState(30); // Monthly
  const [lowRiskFrequency, setLowRiskFrequency] = useState(90); // Quarterly
  const [stableFrequency, setStableFrequency] = useState(180); // Bi-annually

  const frequencyOptions: FrequencyOption[] = [
    { value: 7, label: 'Weekly', checksPerMonth: 4, recommended: false },
    { value: 14, label: 'Bi-Weekly', checksPerMonth: 2, recommended: true },
    { value: 30, label: 'Monthly', checksPerMonth: 1, recommended: true },
    { value: 60, label: 'Bi-Monthly', checksPerMonth: 0.5, recommended: false },
    { value: 90, label: 'Quarterly', checksPerMonth: 0.33, recommended: true },
    { value: 180, label: 'Bi-Annually', checksPerMonth: 0.17, recommended: false },
    { value: 365, label: 'Annually', checksPerMonth: 0.08, recommended: false }
  ];

  // Load existing settings and check Pro subscription when modal opens
  useEffect(() => {
    if (isOpen) {
      loadExistingSettings();
      checkProSubscriptionStatus();
      loadTierState();
    }
  }, [isOpen]);

  const loadTierState = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/subscription/state', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      setTierState({
        source: data.source,
        tierName: data.tier?.displayName || null,
        monthlyQuota: data.totalQuota?.monitoring || 0,
        usedThisCycle: data.cycle?.used || 0,
        monthlyPricePence: data.tier?.monthlyPricePence ?? null
      });
    } catch (err) {
      // Best-effort; banner just won't render.
      console.warn('Tier state load failed:', err);
    }
  };

  const checkProSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/monitoring/pro-subscription-status', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setHasActiveProSubscription(data.hasActiveProSubscription);
      }
    } catch (error) {
      console.error('Error checking Pro subscription status:', error);
    }
  };

  // Calculate estimate whenever settings change
  useEffect(() => {
    if (isOpen && !loadingSettings) {
      calculateEstimate();
    }
  }, [isOpen, frequencyMode, manualFrequency, highRiskFrequency, mediumRiskFrequency, lowRiskFrequency, stableFrequency, loadingSettings]);

  const loadExistingSettings = async () => {
    setLoadingSettings(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/monitoring/projects/${projectId}/settings`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();

        // Set monitoring enabled state
        setMonitoringEnabled(data.monitoringEnabled || false);

        // Load saved settings if they exist
        if (data.monitoringEnabled) {
          setFrequencyMode(data.frequencyMode || 'adaptive');
          setManualFrequency(data.manualFrequency || 90);

          if (data.adaptiveSettings) {
            setHighRiskFrequency(data.adaptiveSettings.highRiskFrequency || 14);
            setMediumRiskFrequency(data.adaptiveSettings.mediumRiskFrequency || 30);
            setLowRiskFrequency(data.adaptiveSettings.lowRiskFrequency || 90);
            setStableFrequency(data.adaptiveSettings.stableFrequency || 180);
          }
        }
      }
    } catch (error) {
      console.error('Error loading monitoring settings:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const calculateEstimate = async () => {
    setEstimating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/monitoring/projects/${projectId}/estimate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          projectName,
          frequencyMode,
          manualFrequency,
          adaptiveSettings: {
            highRiskFrequency,
            mediumRiskFrequency,
            lowRiskFrequency,
            stableFrequency
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        setEstimate(data);
      }
    } catch (error) {
      console.error('Error calculating estimate:', error);
    } finally {
      setEstimating(false);
    }
  };

  const handleDisableMonitoring = async () => {
    if (!window.confirm('Are you sure you want to disable monitoring for this project? Candidates will no longer be checked for changes.')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/monitoring/projects/${projectId}/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMonitoringEnabled(false);

        // Check if user can downgrade or cancel
        if (data.downgradeInfo?.canDowngrade) {
          const downgrade = data.downgradeInfo;
          const shouldDowngrade = window.confirm(
            `✅ Monitoring disabled for this project.\n\n` +
            `💰 You could save £${downgrade.monthlySavings}/month!\n\n` +
            `Your current plan: ${downgrade.currentPlan.name} (${downgrade.currentPlan.priceLabel}/month)\n` +
            `Recommended plan: ${downgrade.recommendedPlan.name} (${downgrade.recommendedPlan.priceLabel}/month)\n\n` +
            `Your remaining projects only need ${fmtNum(downgrade.newTotalEstimatedChecks)} checks/month.\n\n` +
            `Would you like to switch to the ${downgrade.recommendedPlan.name} plan?\n` +
            `(Click OK to go to subscription settings)`
          );

          if (shouldDowngrade) {
            // Redirect to subscription management or open settings
            window.location.href = '/settings?tab=subscription';
          }
        } else if (data.downgradeInfo?.canCancel) {
          const switchToPro = window.confirm(
            `✅ Monitoring disabled.\n\n` +
            `You no longer have any projects with monitoring enabled.\n\n` +
            `Your monitoring subscription (${data.downgradeInfo.currentPlan?.priceLabel || ''}/month) will continue billing until cancelled.\n\n` +
            `Would you like to switch back to MarketMapr Pro (£100/month) to continue using the platform without monitoring?\n\n` +
            `Click OK to manage your subscription.`
          );

          if (switchToPro) {
            window.location.href = '/settings?tab=subscription';
          }
        } else {
          alert('✅ Monitoring disabled successfully');
        }

        onClose();
      } else {
        const error = await response.json();
        alert(`❌ Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error disabling monitoring:', error);
      alert('Failed to disable monitoring');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableMonitoring = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/monitoring/projects/${projectId}/enable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          frequencyMode,
          manualFrequency,
          adaptiveSettings: {
            highRiskFrequency,
            mediumRiskFrequency,
            lowRiskFrequency,
            stableFrequency
          },
          autoAddNewCompanies: true,
          notifications: {
            profileChanges: true,
            jobChanges: true,
            newCompanies: true,
            flightRisks: true
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        const successMessage = monitoringEnabled
          ? `✅ Monitoring settings updated successfully!\n\nNew estimated usage: ${data.estimatedMonthlyChecks} checks/month`
          : `✅ ${data.message}\n\nEstimated usage: ${data.estimatedMonthlyChecks} checks/month`;
        alert(successMessage);
        setMonitoringEnabled(true);
        onClose();
      } else if (response.status === 402) {
        // Subscription required or insufficient credits
        const data = await response.json();

        if (data.requiresSubscription || data.insufficientCredits) {
          const planName = data.recommendedPlan?.productName || 'Recommended plan';
          const planPrice = data.recommendedPlan?.amount ? `£${(data.recommendedPlan.amount / 100).toFixed(0)}/month` : '';
          const checks = data.recommendedPlan?.includedChecks?.toLocaleString() || '';

          // Add Pro cancellation notice if applicable
          const proCancellationNotice = hasActiveProSubscription
            ? '\n\n⚠️ Your £100/month MarketMapr Pro subscription will be automatically cancelled when you subscribe to Monitoring.'
            : '';

          const message = data.requiresSubscription
            ? `📊 Subscription Required\n\nYou need ${fmtNum(data.estimatedMonthlyChecks)} checks/month for ${data.candidateCount} candidates.\n\nRecommended: ${planName}\n${checks} checks/month for ${planPrice}${proCancellationNotice}\n\nWould you like to subscribe now?`
            : `📊 Upgrade Required\n\nYour current plan has ${fmtNum(data.currentPlan?.includedChecks)} checks/month but you need ${fmtNum(data.estimatedMonthlyChecks)} checks/month.\n\nRecommended: ${planName}\n${checks} checks/month for ${planPrice}\n\nWould you like to upgrade now?`;

          if (window.confirm(message)) {
            // Save monitoring settings to localStorage so we can auto-enable after payment
            const pendingSettings = {
              projectId,
              frequencyMode,
              manualFrequency,
              adaptiveSettings: {
                highRiskFrequency,
                mediumRiskFrequency,
                lowRiskFrequency,
                stableFrequency
              },
              autoAddNewCompanies: true,
              notifications: {
                profileChanges: true,
                jobChanges: true,
                newCompanies: true,
                flightRisks: true
              }
            };
            localStorage.setItem('pendingMonitoringSettings', JSON.stringify(pendingSettings));

            // Redirect to Stripe checkout
            const checkoutResponse = await fetch('/api/monitoring/checkout', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                planId: data.recommendedPlan?.id || 'starter',
                projectId,
                monitoringSettings: pendingSettings,
                tolt_referral: (window as any).tolt_referral || undefined
              })
            });

            if (checkoutResponse.ok) {
              const checkoutData = await checkoutResponse.json();
              // Handle both direct subscription (existing customers) and checkout redirect (new customers)
              if (checkoutData.subscriptionCreated) {
                // Direct subscription - also enable monitoring immediately
                const enableResponse = await fetch(`/api/monitoring/projects/${projectId}/enable`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify(pendingSettings)
                });

                // If they upgraded from Pro mid-cycle, the backend credits
                // them for the unused Pro time via Stripe customer balance
                // which auto-applies to their next invoice.
                const creditPence = checkoutData.proration_credit_pence || 0;
                const creditLine = creditPence > 0
                  ? `\n\nWe've added a £${(creditPence / 100).toFixed(2)} credit to your next invoice for the unused portion of your MarketMapr Pro plan.`
                  : '';

                if (enableResponse.ok) {
                  localStorage.removeItem('pendingMonitoringSettings');
                  alert(`✅ Monitoring subscription created and monitoring enabled!${creditLine}`);
                } else {
                  alert(`✅ Subscription created! Monitoring will be enabled when you return to the project.${creditLine}`);
                }
                window.location.href = checkoutData.redirectUrl;
              } else if (checkoutData.checkoutUrl) {
                window.location.href = checkoutData.checkoutUrl;
              }
            } else {
              alert('❌ Failed to create checkout session. Please try again.');
            }
          }
        } else {
          alert(`❌ Error: ${data.error || data.message}`);
        }
      } else if (response.status === 409) {
        const data = await response.json();
        alert(`ℹ️ ${data.message}`);
        onClose();
      } else {
        const error = await response.json();
        alert(`❌ Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error enabling monitoring:', error);
      alert('Failed to enable monitoring');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop flex items-center justify-center p-4">
      <div className="modal-dialog max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-2xl z-10">
          <h2 className="text-xl font-bold text-gray-900 tracking-tight">
            📡 Monitoring Settings
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl transition-colors duration-200"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Project Info */}
          <div className="bg-blue-50 border border-blue-200/80 rounded-xl p-4 shadow-card">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-blue-900 tracking-tight">Project: {projectName}</h3>
                <p className="text-sm text-blue-700 mt-1">
                  {monitoringEnabled ? 'Update monitoring settings for this project' : 'Configure monitoring settings for this project'}
                </p>
              </div>
              {monitoringEnabled && (
                <span className="badge badge-green">
                  ✓ Monitoring Active
                </span>
              )}
            </div>
          </div>

          {/* Tier plan summary (Starter / Growth / Scale) — shows quota, used,
              projected for this project, and remaining. Replaces the legacy
              "subscribe to a monitoring plan" CTA for users already on a tier. */}
          {tierState?.source === 'tiered' && tierState.monthlyQuota > 0 && (() => {
            const projected = estimate?.projectEstimate?.estimatedMonthlyChecks ?? 0;
            const usedAlready = tierState.usedThisCycle;
            const remainingBefore = Math.max(0, tierState.monthlyQuota - usedAlready);
            const remainingAfter = Math.max(0, remainingBefore - projected);
            const tone = projected > remainingBefore
              ? 'red'
              : projected > remainingBefore * 0.7
                ? 'amber'
                : 'green';
            const toneStyles = {
              green: 'bg-green-50 border-green-300',
              amber: 'bg-amber-50 border-amber-300',
              red:   'bg-red-50 border-red-300'
            }[tone];
            return (
              <div className={`border-2 rounded-xl p-4 shadow-card ${toneStyles}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="font-bold text-gray-900">
                      Your {tierState.tierName || 'subscription'} plan
                    </h4>
                    <p className="text-sm text-gray-700 mt-1">
                      <span className="font-semibold">{fmtNum(tierState.monthlyQuota)}</span> monitoring checks included per month.
                      You've used <span className="font-semibold">{fmtNum(usedAlready)}</span> this cycle,
                      leaving <span className="font-semibold">{fmtNum(remainingBefore)}</span> available.
                    </p>
                  </div>
                </div>
                {projected > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-300/40 grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">This project</p>
                      <p className="text-lg font-bold text-gray-900">{fmtNum(projected)}</p>
                      <p className="text-xs text-gray-500">checks/month</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Remaining after</p>
                      <p className={`text-lg font-bold ${tone === 'red' ? 'text-red-700' : tone === 'amber' ? 'text-amber-700' : 'text-green-700'}`}>
                        {fmtNum(remainingAfter)}
                      </p>
                      <p className="text-xs text-gray-500">checks/month</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Status</p>
                      <p className={`text-lg font-bold ${tone === 'red' ? 'text-red-700' : tone === 'amber' ? 'text-amber-700' : 'text-green-700'}`}>
                        {tone === 'red' ? 'Over quota' : tone === 'amber' ? 'Near limit' : 'Within plan'}
                      </p>
                      {tone === 'red' && (
                        <a href="/settings?tab=subscription" className="text-xs text-blue-700 underline">Add a bolt-on</a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Pro Subscription Notice - Only show if user has Pro and monitoring not enabled yet */}
          {hasActiveProSubscription && !monitoringEnabled && !estimate?.currentSubscription && tierState?.source !== 'tiered' && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 shadow-card">
              <div className="flex items-start gap-3">
                <div className="text-2xl">💡</div>
                <div>
                  <h4 className="font-bold text-amber-900 mb-1">MarketMapr Pro Subscription</h4>
                  <p className="text-sm text-amber-800 mb-2">
                    You currently have an active MarketMapr Pro subscription (£100/month).
                  </p>
                  <p className="text-sm text-amber-800 font-medium">
                    When you subscribe to a Monitoring plan, your Pro subscription will be automatically cancelled
                    and replaced with your new Monitoring subscription.
                  </p>
                  <p className="text-xs text-amber-700 mt-2">
                    Note: If you later cancel Monitoring, you'll need to re-subscribe to MarketMapr Pro separately.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Frequency Mode Selection */}
          <div>
            <h3 className="text-lg font-semibold tracking-tight mb-3">Monitoring Frequency</h3>
            <div className="space-y-3">
              {/* Adaptive Mode — the recommended default: risk-weighted checks
                  spend the plan's allowance where change is most likely. The
                  selected card carries the highlight styling. */}
              <label className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${frequencyMode === 'adaptive' ? 'border-blue-500 bg-blue-50 shadow-card' : 'hover:bg-gray-50 hover:shadow-card-hover'}`}>
                <input
                  type="radio"
                  value="adaptive"
                  checked={frequencyMode === 'adaptive'}
                  onChange={(e) => setFrequencyMode(e.target.value as 'adaptive')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-semibold flex items-center gap-2">
                    🧠 Smart Adaptive Monitoring
                    <span className="badge badge-green">Recommended</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Automatically adjusts frequency based on flight risk and tenure.
                    High-risk candidates checked more frequently to save credits.
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    High risk: bi-weekly, Medium: monthly, Stable: quarterly
                  </p>
                </div>
              </label>

              {/* Manual Mode */}
              <label className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${frequencyMode === 'manual' ? 'border-blue-500 bg-blue-50 shadow-card' : 'hover:bg-gray-50 hover:shadow-card-hover'}`}>
                <input
                  type="radio"
                  value="manual"
                  checked={frequencyMode === 'manual'}
                  onChange={(e) => setFrequencyMode(e.target.value as 'manual')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-semibold flex items-center gap-2">
                    📅 Fixed Schedule Monitoring
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Check all candidates at the same interval. Simple and predictable.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Manual Frequency Selector */}
          {frequencyMode === 'manual' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Check Frequency
              </label>
              <select
                value={manualFrequency}
                onChange={(e) => setManualFrequency(parseInt(e.target.value))}
                className="input"
              >
                {frequencyOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label} ({option.checksPerMonth} {option.checksPerMonth === 1 ? 'check' : 'checks'}/month per candidate)
                    {option.recommended ? ' ⭐ Recommended' : ''}
                  </option>
                ))}
              </select>

              {/* Savings indicator */}
              {manualFrequency === 90 && (
                <p className="mt-2 text-sm text-green-600 font-medium">
                  💰 Quarterly monitoring saves 66% compared to monthly checks!
                </p>
              )}
            </div>
          )}

          {/* Adaptive Tier Configuration */}
          {frequencyMode === 'adaptive' && estimate && (
            <div className="border border-gray-200/80 rounded-xl p-4 bg-gray-50 shadow-card">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 tracking-tight">
                Customize Monitoring Frequencies by Risk Tier
              </h3>
              <p className="text-xs text-gray-600 mb-4">
                Adjust how often each risk tier is monitored. Distribution is based on actual candidate tenure data.
              </p>

              <div className="space-y-3">
                {/* High Risk Tier */}
                <div className="bg-white border border-red-200/80 rounded-xl p-3 shadow-card transition-all duration-200 hover:shadow-card-hover">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="font-semibold text-red-900 text-sm">🔴 High Risk</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {estimate.projectEstimate.riskTierDistribution
                          ? `${estimate.projectEstimate.riskTierDistribution.highRisk} candidates (${Math.round((estimate.projectEstimate.riskTierDistribution.highRisk / estimate.projectEstimate.candidateCount) * 100)}%)`
                          : `~${Math.round(estimate.projectEstimate.candidateCount * 0.20)} candidates (20%)`}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        <strong>Criteria:</strong> 0-2 years tenure, "Open to work" signal, recent profile updates (headline, summary, role)
                      </div>
                    </div>
                    <select
                      value={highRiskFrequency}
                      onChange={(e) => setHighRiskFrequency(parseInt(e.target.value))}
                      className="input w-auto flex-shrink-0 ml-2"
                    >
                      {frequencyOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Medium Risk Tier */}
                <div className="bg-white border border-yellow-200/80 rounded-xl p-3 shadow-card transition-all duration-200 hover:shadow-card-hover">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="font-semibold text-yellow-900 text-sm">🟡 Medium Risk</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {estimate.projectEstimate.riskTierDistribution
                          ? `${estimate.projectEstimate.riskTierDistribution.mediumRisk} candidates (${Math.round((estimate.projectEstimate.riskTierDistribution.mediumRisk / estimate.projectEstimate.candidateCount) * 100)}%)`
                          : `~${Math.round(estimate.projectEstimate.candidateCount * 0.30)} candidates (30%)`}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        <strong>Criteria:</strong> 2-4 years tenure, minor profile updates (skills, certifications)
                      </div>
                    </div>
                    <select
                      value={mediumRiskFrequency}
                      onChange={(e) => setMediumRiskFrequency(parseInt(e.target.value))}
                      className="input w-auto flex-shrink-0 ml-2"
                    >
                      {frequencyOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Low Risk Tier */}
                <div className="bg-white border border-blue-200/80 rounded-xl p-3 shadow-card transition-all duration-200 hover:shadow-card-hover">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="font-semibold text-blue-900 text-sm">🔵 Low Risk</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {estimate.projectEstimate.riskTierDistribution
                          ? `${estimate.projectEstimate.riskTierDistribution.lowRisk} candidates (${Math.round((estimate.projectEstimate.riskTierDistribution.lowRisk / estimate.projectEstimate.candidateCount) * 100)}%)`
                          : `~${Math.round(estimate.projectEstimate.candidateCount * 0.30)} candidates (30%)`}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        <strong>Criteria:</strong> 4-7 years tenure, stable employment, established in role
                      </div>
                    </div>
                    <select
                      value={lowRiskFrequency}
                      onChange={(e) => setLowRiskFrequency(parseInt(e.target.value))}
                      className="input w-auto flex-shrink-0 ml-2"
                    >
                      {frequencyOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Stable Tier */}
                <div className="bg-white border border-green-200/80 rounded-xl p-3 shadow-card transition-all duration-200 hover:shadow-card-hover">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="font-semibold text-green-900 text-sm">🟢 Very Stable</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {estimate.projectEstimate.riskTierDistribution
                          ? `${estimate.projectEstimate.riskTierDistribution.stable} candidates (${Math.round((estimate.projectEstimate.riskTierDistribution.stable / estimate.projectEstimate.candidateCount) * 100)}%)`
                          : `~${Math.round(estimate.projectEstimate.candidateCount * 0.20)} candidates (20%)`}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        <strong>Criteria:</strong> 7+ years tenure, no job changes, senior roles, highly stable employment
                      </div>
                    </div>
                    <select
                      value={stableFrequency}
                      onChange={(e) => setStableFrequency(parseInt(e.target.value))}
                      className="input w-auto flex-shrink-0 ml-2"
                    >
                      {frequencyOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="mt-3 p-2 bg-blue-50 border border-blue-200/80 rounded-lg text-xs text-blue-800">
                💡 Tip: Use more frequent monitoring for high-risk candidates to catch changes early, and less frequent monitoring for stable employees to save credits.
              </div>
            </div>
          )}

          {/* Cost Estimate */}
          {estimating ? (
            <div className="bg-gray-50 border border-gray-200/80 rounded-xl p-4 shadow-card animate-fade-in">
              <p className="text-center text-gray-600">Calculating estimate...</p>
            </div>
          ) : estimate && (
            <div className="space-y-4 animate-fade-in">
              <div className="bg-gray-50 border border-gray-200/80 rounded-xl p-4 shadow-card">
                <h3 className="font-semibold tracking-tight mb-3">📊 Cost Estimate</h3>

                {/* This Project */}
                <div className="mb-3">
                  <p className="text-sm font-medium text-gray-700">This Project:</p>
                  <p className="text-lg font-bold text-blue-600">
                    {estimate.projectEstimate.estimatedMonthlyChecks} checks/month
                  </p>
                  <p className="text-xs text-gray-500">
                    {estimate.projectEstimate.candidateCount} candidates × {frequencyMode === 'manual' ? `${frequencyOptions.find(f => f.value === manualFrequency)?.label}` : 'Adaptive'}
                  </p>
                </div>

                {/* Existing Projects */}
                {estimate.aggregateEstimate.existingChecks > 0 && (
                  <div className="mb-3 pt-3 border-t">
                    <p className="text-sm font-medium text-gray-700">Existing Monitored Projects:</p>
                    <div className="mt-1 space-y-1">
                      {estimate.aggregateEstimate.projectBreakdown
                        .filter(p => p.projectId !== 'new')
                        .map((project, idx) => (
                          <p key={idx} className="text-xs text-gray-600 ml-2">
                            • {project.projectName}: {project.estimatedChecks} checks/month
                          </p>
                        ))
                      }
                    </div>
                  </div>
                )}

                {/* Total */}
                <div className="pt-3 border-t">
                  <p className="text-sm font-medium text-gray-700">Total Across All Projects:</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {estimate.aggregateEstimate.totalEstimatedChecks} checks/month
                  </p>
                </div>

                {/* Overage Warning — Tiered users (Starter / Growth / Scale).
                    Compares bolt-on cost vs cheapest tier upgrade and shows
                    whichever is cheaper as the headline recommendation. */}
                {tierState?.source === 'tiered' && estimate.tieredUpsell && estimate.tieredUpsell.shortfall > 0 && estimate.tieredUpsell.recommended && (() => {
                  const upsell = estimate.tieredUpsell;
                  const rec = upsell.recommended!;
                  const stay = upsell.paths.find(p => p.kind === 'stay');
                  const formatPrice = (pence: number) => `£${(pence / 100).toFixed(0)}`;
                  return (
                    <div className="mt-4 bg-red-50 border-2 border-red-300 rounded-xl p-4 shadow-card">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">⚠️</div>
                        <div className="flex-1">
                          <h4 className="font-bold text-red-900 mb-2 tracking-tight">Plan Capacity Exceeded</h4>
                          <p className="text-sm text-red-800 mb-3">
                            Your estimated usage ({fmtNum(upsell.neededChecks)} checks/month)
                            exceeds your current quota ({fmtNum(upsell.currentQuota)} checks/month).
                          </p>

                          <div className="bg-white border border-red-200 rounded-lg p-3 mb-3">
                            <p className="text-sm font-semibold text-red-900 mb-1">Overage:</p>
                            <p className="text-2xl font-bold text-red-600">
                              +{fmtNum(upsell.shortfall)} checks/month
                            </p>
                          </div>

                          <div className="space-y-2">
                            <p className="text-sm font-semibold text-red-900">
                              {rec.kind === 'tier_upgrade' ? 'Cheapest option: upgrade your plan' : 'Cheapest option: add bolt-ons'}
                            </p>

                            {/* Recommended path */}
                            <div className="bg-white border-2 border-green-400 rounded-lg p-3">
                              <div className="flex justify-between items-start gap-2">
                                <div>
                                  <p className="text-sm font-semibold text-gray-900 mb-1">
                                    {rec.kind === 'tier_upgrade'
                                      ? `✅ Upgrade to ${rec.tierName}`
                                      : `✅ Add ${rec.boltOnsToAdd} monitoring bolt-on${rec.boltOnsToAdd > 1 ? 's' : ''}`}
                                    <span className="ml-2 badge badge-green text-xs">Recommended</span>
                                  </p>
                                  <p className="text-xs text-gray-700">
                                    {rec.kind === 'tier_upgrade'
                                      ? <>
                                          {fmtNum(rec.tierBaseQuota)} checks/mo included
                                          {rec.boltOnsToAdd > 0 && <> + {rec.boltOnsToAdd} bolt-on{rec.boltOnsToAdd > 1 ? 's' : ''}</>}
                                          {' '}→ {fmtNum(rec.totalQuotaAfter)} checks/mo
                                        </>
                                      : <>
                                          +{fmtNum((rec.boltOnsToAdd * rec.boltOnQuotaPerUnit))} checks/mo
                                          {' '}→ {fmtNum(rec.totalQuotaAfter)} total
                                        </>}
                                  </p>
                                  {upsell.savingsVsStayPence > 0 && stay && rec.kind === 'tier_upgrade' && (
                                    <p className="text-xs text-green-700 mt-1 font-medium">
                                      Saves {formatPrice(upsell.savingsVsStayPence)}/mo vs adding {stay.boltOnsToAdd} bolt-on{stay.boltOnsToAdd > 1 ? 's' : ''} ({formatPrice(stay.totalMonthlyPence)}/mo)
                                    </p>
                                  )}
                                </div>
                                <p className="text-lg font-bold text-green-700 whitespace-nowrap">
                                  {formatPrice(rec.totalMonthlyPence)}/mo
                                </p>
                              </div>
                              <div className="mt-2">
                                <a
                                  href="/settings?tab=subscription"
                                  className="text-xs text-blue-700 underline"
                                >
                                  {rec.kind === 'tier_upgrade' ? 'Upgrade your plan →' : 'Add bolt-on →'}
                                </a>
                              </div>
                            </div>

                            {/* Alternative path — show the other option if it exists and is different */}
                            {rec.kind === 'tier_upgrade' && stay && (
                              <details className="bg-white border border-gray-200 rounded-lg p-3">
                                <summary className="text-xs text-gray-700 cursor-pointer">
                                  Or: stay on {stay.tierName} + add {stay.boltOnsToAdd} bolt-on{stay.boltOnsToAdd > 1 ? 's' : ''} ({formatPrice(stay.totalMonthlyPence)}/mo)
                                </summary>
                                <p className="text-xs text-gray-600 mt-2">
                                  {fmtNum(stay.totalQuotaAfter)} checks/mo total. Costs {formatPrice(stay.totalMonthlyPence - rec.totalMonthlyPence)}/mo more than the recommended upgrade.
                                </p>
                              </details>
                            )}

                            <div className="bg-white border border-red-200 rounded-lg p-3">
                              <p className="text-sm font-semibold text-gray-900 mb-1">
                                ⚙️ Or: reduce monitoring frequencies
                              </p>
                              <p className="text-xs text-gray-700">
                                Adjust the dropdowns above to lower your estimated usage to fit your current quota.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Overage Warning — Legacy (non-tiered) users. Keeps the old
                    "upgrade to legacy monitoring plan" flow. */}
                {tierState && tierState.source !== 'tiered' && estimate.recommendedPlan && estimate.currentSubscription && estimate.currentSubscription.includedChecks > 0 &&
                 estimate.aggregateEstimate.totalEstimatedChecks > estimate.currentSubscription.includedChecks && (
                  <div className="mt-4 bg-red-50 border-2 border-red-300 rounded-xl p-4 shadow-card">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">⚠️</div>
                      <div className="flex-1">
                        <h4 className="font-bold text-red-900 mb-2 tracking-tight">Plan Capacity Exceeded</h4>
                        <p className="text-sm text-red-800 mb-3">
                          Your estimated usage ({fmtNum(estimate.aggregateEstimate.totalEstimatedChecks)} checks/month)
                          exceeds your current plan capacity ({fmtNum(estimate.currentSubscription.includedChecks)} checks/month).
                        </p>

                        <div className="bg-white border border-red-200 rounded-lg p-3 mb-3">
                          <p className="text-sm font-semibold text-red-900 mb-1">Overage:</p>
                          <p className="text-2xl font-bold text-red-600">
                            +{fmtNum((estimate.aggregateEstimate.totalEstimatedChecks - estimate.currentSubscription.includedChecks))} checks/month
                          </p>
                          <p className="text-xs text-red-700 mt-1">
                            over your {estimate.currentSubscription.planName || 'current'} plan limit
                          </p>
                        </div>

                        <div className="space-y-2">
                          <p className="text-sm font-semibold text-red-900">You have two options:</p>
                          <div className="bg-white border border-red-200 rounded-lg p-3">
                            <p className="text-sm font-semibold text-gray-900 mb-1">
                              ✅ Option 1: Upgrade to {estimate.recommendedPlan.name}
                            </p>
                            <p className="text-xs text-gray-700 mb-2">
                              {fmtNum(estimate.recommendedPlan.includedChecks)} checks/month included
                              • {estimate.recommendedPlan.priceLabel}/mo
                            </p>
                            <p className="text-xs text-green-700">
                              Covers your usage with 10% buffer for growth
                            </p>
                          </div>
                          <div className="bg-white border border-red-200 rounded-lg p-3">
                            <p className="text-sm font-semibold text-gray-900 mb-1">
                              ⚙️ Option 2: Reduce monitoring frequencies
                            </p>
                            <p className="text-xs text-gray-700">
                              Adjust the dropdowns above to lower your estimated usage to fit within your current plan
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recommended Plan — Legacy users only. Tiered users already see
                    their quota box at the top of the modal and (when over) the
                    smart bolt-on-vs-upgrade comparison above. */}
                {tierState && tierState.source !== 'tiered' && estimate.recommendedPlan && (
                  <div className="mt-4 bg-white border-2 border-blue-300 rounded-xl p-3 shadow-card">
                    <p className="text-sm font-medium text-gray-700 mb-2">Recommended Plan:</p>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-bold text-blue-900">{estimate.recommendedPlan.name}</p>
                        <p className="text-xs text-gray-600">
                          {fmtNum(estimate.recommendedPlan.includedChecks)} checks/month included
                        </p>
                      </div>
                      <p className="text-2xl font-bold text-blue-600">
                        {estimate.recommendedPlan.priceLabel}/mo
                      </p>
                    </div>

                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200/80 rounded-lg">
                      <p className="text-xs text-blue-800">
                        💡 Includes 10% buffer for candidates moving up risk tiers (e.g., profile updates → higher frequency)
                      </p>
                    </div>

                    {estimate.needsUpgrade && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200/80 rounded-lg">
                        <p className="text-xs text-yellow-800">
                          ⚠️ You'll need a subscription to enable monitoring. Contact us to set up your plan.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notification Preferences. Shown regardless of monitoring being
              enabled: the recipients panel also controls the daily jobs
              email, which jobs-only clients need to reach. */}
          {(
            <div className="border border-slate-200/80 rounded-xl overflow-hidden shadow-card">
              <button
                onClick={() => setShowNotificationPrefs(!showNotificationPrefs)}
                className="w-full px-4 py-3 bg-slate-50 flex justify-between items-center hover:bg-slate-100 transition-all duration-200"
              >
                <span className="font-semibold text-gray-900 text-sm">Email Notification Preferences</span>
                <span className="text-gray-500 text-sm">{showNotificationPrefs ? '▲' : '▼'}</span>
              </button>
              {showNotificationPrefs && (
                <div className="px-4 py-4 bg-slate-900 border-t border-slate-200">
                  <NotificationPreferences projectId={projectId} projectName={projectName} />
                </div>
              )}
            </div>
          )}

          {/* ⚡ Instant check — check everyone in the project right now */}
          <div className="p-4 border border-amber-200 bg-amber-50 rounded-xl">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="font-semibold text-gray-900">⚡ Instant check</div>
                <p className="text-sm text-gray-600 mt-0.5">
                  Check every monitored candidate in this project <strong>right now</strong>, instead of
                  waiting for their schedule. Consumes <strong>1 monitoring credit per candidate</strong> from
                  your cycle allowance.
                </p>
                {instantMsg && (
                  <p className="text-sm text-amber-800 mt-1.5 font-medium">
                    {instantMsg}
                    {instantRunning && instantOpId && (
                      <button
                        onClick={handleStopInstantCheck}
                        className="ml-3 px-2.5 py-0.5 rounded-md bg-white border border-amber-300 text-amber-800 text-xs font-semibold hover:bg-amber-100 transition-colors"
                        title="Stop after the current batch — only checked candidates consume credits"
                      >
                        ■ Stop
                      </button>
                    )}
                  </p>
                )}
              </div>
              <button
                disabled={instantRunning}
                onClick={handleInstantCheck}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:bg-gray-400 text-sm font-medium whitespace-nowrap"
              >
                {instantRunning ? '⏳ Checking…' : 'Run instant check'}
              </button>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-2xl">
          {estimate?.currentSubscription &&
           estimate.currentSubscription.includedChecks > 0 &&
           estimate.aggregateEstimate.totalEstimatedChecks > estimate.currentSubscription.includedChecks && (
            <div className="mb-3 p-3 bg-red-50 border border-red-300 rounded-xl">
              <p className="text-sm font-semibold text-red-900 mb-1">
                ⚠️ Cannot enable: Plan capacity exceeded
              </p>
              <p className="text-xs text-red-700">
                {estimate.recommendedPlan?.name
                  ? <>Reduce your monitoring frequencies or upgrade to the {estimate.recommendedPlan.name} plan to continue.</>
                  : <>Reduce your monitoring frequencies, or add monitoring checks to your plan in Settings → Subscription, to continue.</>}
              </p>
            </div>
          )}

          {!canManage && (
            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Only a team admin can switch monitoring on or off or change these settings. Ask your admin to make the change.
            </div>
          )}
          <div className="flex gap-3 justify-between">
            {/* Left side - Disable button (only when monitoring is enabled) */}
            <div>
              {monitoringEnabled && canManage && (
                <button
                  onClick={handleDisableMonitoring}
                  disabled={loading}
                  className="btn-danger"
                >
                  {loading ? '⏳ Disabling...' : '🛑 Disable Monitoring'}
                </button>
              )}
            </div>

            {/* Right side - Cancel and Enable/Update buttons */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              {canManage && (
              <button
                onClick={handleEnableMonitoring}
                disabled={
                  loading ||
                  estimating ||
                  loadingSettings ||
                  (estimate?.currentSubscription &&
                   estimate.currentSubscription.includedChecks > 0 &&
                   estimate.aggregateEstimate.totalEstimatedChecks > estimate.currentSubscription.includedChecks)
                }
                className="btn-primary flex items-center gap-2"
              >
                {loading ? (monitoringEnabled ? '⏳ Updating...' : '⏳ Enabling...') : (monitoringEnabled ? '💾 Update Settings' : '📡 Enable Monitoring')}
              </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// A crash inside the settings (e.g. data in a shape the screen didn't
// expect) used to blank the whole page (Karl Lightfoot, 3 Nov 2026): show a
// message with a Close button instead, and log the error for us.
class MonitoringSettingsBoundary extends React.Component<{ onClose: () => void; children: React.ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error) { console.error('[monitoring settings] crashed:', error); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
          <h3 className="text-lg font-semibold text-gray-900">Monitoring settings couldn't open</h3>
          <p className="text-sm text-gray-600 mt-2">Something on this screen went wrong. Your project and its monitoring are unchanged. Please try again, and if it keeps happening let us know.</p>
          <p className="text-xs text-gray-400 mt-2 break-words">{String(this.state.error.message || this.state.error)}</p>
          <div className="flex justify-end mt-4">
            <button onClick={() => { this.setState({ error: null }); this.props.onClose(); }} className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm">Close</button>
          </div>
        </div>
      </div>
    );
  }
}

const MonitoringSettingsModalSafe: React.FC<MonitoringSettingsModalProps> = (props) => (
  <MonitoringSettingsBoundary onClose={props.onClose}>
    <MonitoringSettingsModal {...props} />
  </MonitoringSettingsBoundary>
);

export default MonitoringSettingsModalSafe;
